import { SETTINGSSTATE, THROTTLE_TIMERS } from '.'
import {
    SecureStateType,
    SnapSize,
    TimerEvent,
    UserEvent,
} from '../constants/enums'
import {
    previewSnapshotUrl,
    SNAP_PENDING_PLACEHOLDER,
} from '../mfcLogic/avatarPreviewUrl'
import { getTimeoutFromAnimationUeopt } from '../mfcLogic/settings'
import { randomize } from '../utilities/math'
import { getStateUser } from './getState'
import { registerTimer, unregisterTimer } from './internal/timers'
import { pubSubSecureState, pubSubTimer, pubSubUsers } from './pubSub'

export { registerTimer, unregisterTimer }

const SNAPSIZEMAP = {
    '341x192': 0,
    '427x240': 1,
    '853x480': 2,
}

export const pauseTimers = (pause = true) => {
    THROTTLE_TIMERS.paused = pause
}

const initialDelay = (throttle: number, min = 0) => randomize(min, throttle)

/**
 * Re-resolves every registered snap still showing the pending placeholder.
 *
 * `registerSnaps` builds a tile's URL once, when the tile mounts, and only
 * the `snaps` interval rebuilds it — after the live model list has loaded
 * and each tile's throttle has elapsed. Server-seeded tiles mount before
 * the age-gate state and the snaps token settle, so under an active gate
 * they register the placeholder and would hold it for that whole span.
 * This runs when either input settles and swaps the placeholder for the
 * real URL immediately; entries already showing a snapshot are left alone,
 * so the hourly token refresh never re-requests every visible snap.
 *
 * Static entries are refreshed too — the interval never rebuilds those, so
 * this is their only path off the placeholder — and keep their static flag.
 * The timestamp is reset so the interval does not request a second
 * snapshot right after this one.
 */
export const refreshPendingSnaps = () => {
    const now = Date.now()

    Object.keys(THROTTLE_TIMERS.snaps).forEach((snapKey) => {
        const posterTimer = THROTTLE_TIMERS.snaps[snapKey]
        if (!posterTimer || posterTimer.srcUrl !== SNAP_PENDING_PLACEHOLDER) {
            return
        }

        const srcUrl = previewSnapshotUrl(Number(snapKey), {
            large: posterTimer.size === SnapSize.large,
            xsmall: posterTimer.size === SnapSize.small,
        })
        if (srcUrl === SNAP_PENDING_PLACEHOLDER) {
            return
        }

        const refreshed = { ...posterTimer, srcUrl, timestamp: now }
        THROTTLE_TIMERS.snaps[snapKey] = refreshed
        pubSubTimer.emit(`${TimerEvent.STATE}::${snapKey}`, refreshed, true)
    })
}

if (typeof window !== 'undefined') {
    // both inputs of the placeholder decision (see previewSnapshotUrl)
    pubSubSecureState.on(
        SecureStateType.IMAGE_SECURITY_CONFIG,
        refreshPendingSnaps,
    )
    pubSubSecureState.on(SecureStateType.AGE_VERIFICATION, refreshPendingSnaps)
}

export const registerSnaps = (
    id: number,
    size: SnapSize,
    addThrottleWobble = true,
    // calcNow = true,
) => {
    const snapKey = `${id}`
    const posterTimer = THROTTLE_TIMERS.snaps[snapKey]
    const currentOverride = posterTimer?.staticImageOverride || false
    const throttle = getTimeoutFromAnimationUeopt(
        SETTINGSSTATE.DEFAULT.animationSpeed,
    )
    const foundThrottle = throttle ? throttle : (posterTimer?.throttle ?? 1000)
    const foundTimestamp = addThrottleWobble
        ? initialDelay(foundThrottle, foundThrottle / 2)
        : posterTimer?.timestamp || 0
    const foundSize =
        posterTimer?.size && SNAPSIZEMAP[posterTimer.size] > SNAPSIZEMAP[size]
            ? posterTimer.size
            : size

    const user = getStateUser(id)
    if (!user) {
        const unsub = pubSubUsers.on(UserEvent.UPDATE + '::' + id, () => {
            // user object will have been loaded now with all needed attributes populated
            unsub()
            const currentPosterTimer = THROTTLE_TIMERS.snaps[snapKey]
            if (currentPosterTimer && !currentPosterTimer.srcUrl) {
                currentPosterTimer.srcUrl = previewSnapshotUrl(id, {
                    large: size === SnapSize.large,
                    xsmall: size === SnapSize.small,
                })
                currentPosterTimer.timestamp = Date.now()
                pubSubTimer.emit(
                    `${TimerEvent.STATE}::${snapKey}`,
                    currentPosterTimer,
                )
            }
        })
    }

    const newPosterTimer = {
        size: foundSize,
        throttle: addThrottleWobble
            ? Math.max(foundThrottle + randomize(-500, 500), 1000)
            : foundThrottle,
        subbed: true,
        staticImage: false,
        staticImageOverride: currentOverride,
        timestamp: user ? foundTimestamp : 0,
        srcUrl: user
            ? previewSnapshotUrl(id, {
                  large: size === SnapSize.large,
                  xsmall: size === SnapSize.small,
              })
            : '',
    }

    THROTTLE_TIMERS.snaps[snapKey] = newPosterTimer

    return newPosterTimer.srcUrl
}

export const forceSnapUpdate = (id: number) => {
    const snapKey = `${id}`
    const posterTimer = THROTTLE_TIMERS.snaps[snapKey]
    if (
        posterTimer &&
        posterTimer.subbed &&
        posterTimer.staticImage &&
        posterTimer.srcUrl
    ) {
        // if we're forcing because we've changed from being static
        // and we already have a src url then we shouldn't calc now,
        // we should delay a full cycle so we aren't immediately
        // updating all the snaps
        posterTimer.subbed = true
        posterTimer.staticImage = false
        posterTimer.staticImageOverride = false
        posterTimer.timestamp = Date.now()
        /// we should be careful about blasting away the reference in certain cases... this is probably one of them?
        // THROTTLE_TIMERS.snaps[snapKey] = newPosterTimer
    } else if (!posterTimer || !posterTimer.subbed) {
        // console.log('[forceSnapUpdate] registering', id, idToSlug[id])
        registerSnaps(id, posterTimer?.size || SnapSize.large)
    }
}
export const setStaticImage = (id: number, staticValue: boolean) => {
    const snapKey = `${id}`
    const posterTimer = THROTTLE_TIMERS.snaps[snapKey]
    // console.trace('[setStaticImage]', id, idToSlug[id], posterTimer)
    if (posterTimer && !posterTimer.staticImageOverride) {
        posterTimer.staticImage = staticValue
        THROTTLE_TIMERS.snaps[snapKey] = { ...posterTimer }
    }
}
export const setStaticImageOverride = (id: number) => {
    const snapKey = `${id}`
    const posterTimer = THROTTLE_TIMERS.snaps[snapKey]
    // console.log('[setStaticImageOverride]', id, idToSlug[id], posterTimer)
    if (posterTimer) {
        posterTimer.staticImageOverride = true
        posterTimer.staticImage = false
        THROTTLE_TIMERS.snaps[snapKey] = { ...posterTimer }
    }
}
